import {
  reportCanvasComponents,
  type WorkbookDefinition,
  type WorkbookOutput,
} from "@erp/shared-types";

type OutputTables = { sheets: readonly { id: string }[] };

/**
 * The Report dataset a Report component charts when it names none: the first
 * Worksheet's. Undefined without Worksheets.
 */
export function defaultReportDatasetKey(
  definition: OutputTables,
): string | undefined {
  return definition.sheets[0]?.id;
}

/**
 * The Worksheets an Output reads, each once, in the order it reads them: a
 * File's tabs, the Worksheets a Report's components chart, or none for an
 * App (it reads its own feeds). An Output
 * computes nothing, so this is what running up to it computes and what its
 * Current / Stale derives from. A removed Worksheet reads as nothing.
 */
export function outputSheetIds(
  definition: OutputTables,
  output: WorkbookOutput,
): string[] {
  const sheets = new Set(definition.sheets.map((sheet) => sheet.id));
  const defaultKey = defaultReportDatasetKey(definition);
  const read = outputReads(output, defaultKey);
  return [
    ...new Set(
      read.filter((id): id is string => id !== undefined && sheets.has(id)),
    ),
  ];
}

function outputReads(
  output: WorkbookOutput,
  defaultKey: string | undefined,
): (string | undefined)[] {
  switch (output.kind) {
    case "sink":
      return [output.sheetId];
    case "file":
      return output.tabs.map((tab) => tab.sheetId);
    case "report":
      return reportCanvasComponents(output.layout).map(
        (component) => component.datasetKey ?? defaultKey,
      );
    case "app":
      return [];
  }
}

/** The Output `id` names, or undefined when it names none. */
export function findOutput(
  definition: Pick<WorkbookDefinition, "outputs">,
  id: string | null | undefined,
): WorkbookOutput | undefined {
  return definition.outputs.find((output) => output.id === id);
}
